import React, { useEffect, useState } from 'react';
import {
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  Button,
  Divider,
  Stack,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Tooltip,
  Fade,
  Zoom,
  Grow,
  Slide,
  useMediaQuery,
  useTheme
} from '@mui/material';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import {
  ShoppingCart,
  FavoriteBorder,
  Share,
  LocalOffer,
  AccessTime,
  Event,
  PriceChange,
  Star,
  StarBorder,
  FilterList
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';

// Utility function to group offers by platform
const groupOffersByPlatform = (offers) => {
  const grouped = {};
  offers.forEach((offer) => {
    const platform = offer.platform || 'Unknown';
    if (!grouped[platform]) {
      grouped[platform] = [];
    }
    grouped[platform].push(offer);
  });
  return grouped;
};

const ResendOfferList = ({ resellerId: propResellerId, hideControls = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState('All');
  const [sortBy, setSortBy] = useState('newest');
  const [expandedCards, setExpandedCards] = useState({});
  const [search, setSearch] = useState('');

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryResellerId = queryParams.get('resellerId');

  const resellerId = propResellerId || queryResellerId;

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://reviewhub-lc.onrender.com/api/resendoffers');
        let data = response.data;

        if (resellerId) {
          data = data.filter((offer) => offer.resellerId === resellerId);
        }

        // Sort data based on selected option
        data = sortOffers(data, sortBy);

        setOffers(data);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };

    fetchOffers();
  }, [resellerId, sortBy]);

  const sortOffers = (data, sortOption) => {
    const sorted = [...data];
    switch (sortOption) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.startDateTime) - new Date(a.startDateTime));
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.startDateTime) - new Date(b.startDateTime));
      case 'price-low':
        return sorted.sort((a, b) => a.offerPrice - b.offerPrice);
      case 'price-high':
        return sorted.sort((a, b) => b.offerPrice - a.offerPrice);
      case 'discount':
        return sorted.sort((a, b) => {
          const discountA = ((a.originalPrice - a.offerPrice) / a.originalPrice) * 100;
          const discountB = ((b.originalPrice - b.offerPrice) / b.originalPrice) * 100;
          return discountB - discountA;
        });
      default:
        return sorted;
    }
  };

  const handlePlatformChange = (e) => {
    setSelectedPlatform(e.target.value);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  const toggleCardExpand = (id) => {
    setExpandedCards(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Search filter
  const searchFilter = (offer) => {
    const searchLower = search.toLowerCase();
    return (
      offer.productName?.toLowerCase().includes(searchLower) ||
      offer.platform?.toLowerCase().includes(searchLower) ||
      String(offer.offerPrice).includes(searchLower) ||
      String(offer.originalPrice).includes(searchLower)
    );
  };

  const getTimeRemaining = (endDateTime) => {
    const now = new Date();
    const end = new Date(endDateTime);
    const diff = end - now;

    if (diff <= 0) return 'Expired';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);

    if (days > 0) return `Ends in ${days}d ${hours}h`;
    if (hours > 0) return `Ends in ${hours}h ${minutes}m`;
    return `Ends in ${minutes}m`;
  };

  const filteredOffers =
    selectedPlatform === 'All'
      ? offers.filter(searchFilter)
      : offers.filter((offer) => offer.platform === selectedPlatform && searchFilter(offer));

  const groupedOffers = groupOffersByPlatform(filteredOffers);

  const platformOptions = ['All', 'Myntra', 'Meesho', 'Flipkart', 'Amazon', 'Instagram'];

  const platformColors = {
    Flipkart: '#2874f0',
    Meesho: '#9c27b0',
    Myntra: '#ff3f6c',
    Amazon: '#ff9900',
    Instagram: '#e1306c',
    Unknown: 'grey',
  };

  const platformIcons = {
    Flipkart: '🛒',
    Meesho: '👗',
    Myntra: '👠',
    Amazon: '📦',
    Instagram: '📷',
    Unknown: '❓',
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="60vh"
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <CircularProgress size={60} thickness={4} color="secondary" />
        </motion.div>
      </Box>
    );
  }

  return (
    <Box p={isMobile ? 1 : 3}>
      {!hideControls && (
        <>
          <Slide direction="down" in={true} mountOnEnter unmountOnExit>
            <Box mb={4}>
              <Typography 
                variant={isMobile ? "h5" : "h4"} 
                fontWeight="bold" 
                gutterBottom
                sx={{
                  background: 'linear-gradient(45deg, #e6395f, #FF8E53 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Hot Deals & Offers
              </Typography>
              <Typography variant={isMobile ? "body2" : "subtitle1"} color="text.secondary">
                Exclusive discounts from top platforms
              </Typography>
            </Box>
          </Slide>

          <Box display="flex" mb={4} flexWrap="wrap" gap={2}>
            <FormControl sx={{ flex: 1, minWidth: isMobile ? '100%' : 'auto' }}>
              <Box display="flex" alignItems="center" sx={{ bgcolor: '#f5f5f5', borderRadius: 2, px: 2 }}>
                <SearchIcon color="action" sx={{ mr: 1 }} />
                <input
                  type="text"
                  placeholder="Search by name, price, or platform"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    outline: 'none',
                    fontSize: isMobile ? '0.875rem' : '1rem',
                    width: '100%',
                    padding: isMobile ? '8px 0' : '12px 0'
                  }}
                />
              </Box>
            </FormControl>
            
            <FormControl sx={{ minWidth: isMobile ? '47%' : 200 }}>
              <InputLabel>
                <Box display="flex" alignItems="center">
                  <FilterList fontSize="small" sx={{ mr: 1 }} />
                  {isMobile ? '' : 'Platform'}
                </Box>
              </InputLabel>
              <Select
                value={selectedPlatform}
                onChange={handlePlatformChange}
                label={isMobile ? '' : 'Platform'}
                size={isMobile ? 'small' : 'medium'}
              >
                {platformOptions.map((platform) => (
                  <MenuItem key={platform} value={platform}>
                    <Box display="flex" alignItems="center">
                      <span style={{ marginRight: 8 }}>{platformIcons[platform] || '❓'}</span>
                      {platform}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: isMobile ? '47%' : 200 }}>
              <InputLabel size={isMobile ? 'small' : 'medium'}>Sort By</InputLabel>
              <Select 
                value={sortBy} 
                onChange={handleSortChange} 
                label="Sort By"
                size={isMobile ? 'small' : 'medium'}
              >
                <MenuItem value="newest">Newest First</MenuItem>
                <MenuItem value="oldest">Oldest First</MenuItem>
                <MenuItem value="price-low">Price: Low to High</MenuItem>
                <MenuItem value="price-high">Price: High to Low</MenuItem>
                <MenuItem value="discount">Highest Discount</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </>
      )}

      {Object.entries(groupedOffers).map(([platform, platformOffers]) => (
        <Box key={platform} mb={6}>
          <Fade in={true}>
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              mb={3} 
              sx={{
                display: 'flex',
                alignItems: 'center',
                '&::after': {
                  content: '""',
                  flex: 1,
                  ml: 2,
                  height: '1px',
                  backgroundColor: 'divider',
                }
              }}
            >
              <Chip
                label={platform}
                size={isMobile ? "small" : "medium"}
                sx={{
                  bgcolor: platformColors[platform] || 'grey',
                  color: 'white',
                  fontWeight: 'bold',
                  px: 1,
                  py: isMobile ? 0.5 : 1.5,
                }}
                icon={<LocalOffer fontSize={isMobile ? "small" : "medium"} />}
              />
            </Typography>
          </Fade>

          <Grid container spacing={isMobile ? 1 : 3}>
            {platformOffers.map((offer, index) => {
              const discount = Math.round(((offer.originalPrice - offer.offerPrice) / offer.originalPrice) * 100);
              const isExpired = offer.endDateTime && new Date() > new Date(offer.endDateTime);
              const isNew = offer.startDateTime && (new Date() - new Date(offer.startDateTime)) < 7 * 24 * 60 * 60 * 1000;
              const isExpanded = expandedCards[offer._id];

              return (
                <Grid item xs={6} sm={6} md={4} lg={3} key={offer._id}>
                  <Grow in={true} timeout={index * 150} >
                    <Card
                      elevation={isExpanded ? 8 : 4}
                      sx={{
                        width: isMobile ? 160 : 240,
                        height: isMobile ? 300 : 400,
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      <Box position="relative" sx={{ height: isMobile ? 120 : 180 }}>
                        {offer.productImage && (
                          <CardMedia
                            component="img"
                            sx={{
                              width: '100%',
                              height: '100%',
                              bgcolor: '#f5f5f5',
                              
                            }}
                            image={offer.productImage}
                            alt={offer.productName}
                          />
                        )}

                        <Box
                          position="absolute"
                          top={8}
                          left={8}
                          display="flex"
                          flexDirection="column"
                          gap={1}
                        >
                          {discount > 0 && (
                            <Chip
                              label={`${discount}% OFF`}
                              size="small"
                              sx={{
                                bgcolor: 'error.main',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: isMobile ? '0.7rem' : '0.8125rem'
                              }}
                            />
                          )}
                        </Box>
                      </Box>

                      <CardContent sx={{ 
                        flexGrow: 1, 
                        p: isMobile ? 1 : 2,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}>
                        <Box>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Typography 
                              variant={isMobile ? "body2" : "h6"} 
                              gutterBottom 
                              sx={{ 
                                fontWeight: 600,
                                fontSize: isMobile ? '0.875rem' : '1rem',
                                lineHeight: 1.2
                              }}
                            >
                              {offer.productName}
                            </Typography>
                            {offer.rating && (
                              <Chip
                                size="small"
                                icon={<Star fontSize="small" color="warning" />}
                                label={offer.rating}
                                sx={{ 
                                  ml: 1, 
                                  fontSize: isMobile ? '0.7rem' : '0.8125rem',
                                  height: '24px'
                                }}
                              />
                            )}
                          </Box>

                          <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                            <Typography 
                              variant={isMobile ? "body2" : "body1"} 
                              sx={{ 
                                textDecoration: 'line-through',
                                fontSize: isMobile ? '0.75rem' : '0.875rem'
                              }}
                            >
                              ₹{offer.originalPrice || 'N/A'}
                            </Typography>
                            <Typography 
                              variant={isMobile ? "body1" : "h6"} 
                              color="error.main"
                              sx={{
                                fontSize: isMobile ? '0.875rem' : '1.125rem'
                              }}
                            >
                              ₹{offer.offerPrice || 'N/A'}
                            </Typography>
                          </Stack>

                          {discount > 0 && (
                            <Box mt={1}>
                              <motion.div
                                initial={{ scale: 0.9 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 0.5 }}
                              >
                                <Chip
                                  label={`Save ₹${offer.originalPrice - offer.offerPrice}`}
                                  size="small"
                                  color="error"
                                  sx={{ 
                                    fontWeight: 'bold',
                                    fontSize: isMobile ? '0.7rem' : '0.8125rem'
                                  }}
                                />
                              </motion.div>
                            </Box>
                          )}

                          <Box mt={1}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Event fontSize="small" color="action" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }} />
                              <Typography 
                                variant={isMobile ? "caption" : "body2"} 
                                color="text.secondary"
                                sx={{ fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                              >
                                {getTimeRemaining(offer.endDateTime)}
                              </Typography>
                            </Stack>
                          </Box>
                        </Box>

                        <Box mt={2}>
                          {offer.productLink && (
                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                size={isMobile ? "small" : "medium"}
                                href={offer.productLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                endIcon={<ShoppingCart fontSize={isMobile ? "small" : "medium"} />}
                                disabled={isExpired}
                                sx={{ 
                                  fontSize: isMobile ? '0.7rem' : '0.8125rem',
                                  py: isMobile ? 0.5 : 1
                                }}
                              >
                                {isExpired ? 'Expired' : 'Buy Now'}
                              </Button>
                            </motion.div>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grow>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}

      {filteredOffers.length === 0 && !loading && (
        <Box textAlign="center" py={10}>
          <Zoom in={true}>
            <Box>
              <Typography variant={isMobile ? "h6" : "h5"} color="text.secondary" gutterBottom>
                No offers found
              </Typography>
              <Typography variant={isMobile ? "body2" : "body1"} color="text.secondary">
                Try changing your filters or check back later for new offers
              </Typography>
            </Box>
          </Zoom>
        </Box>
      )}
    </Box>
  );
};

export default ResendOfferList;